import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { HomeworkTracker, HomeworkLegend } from "@/components/HomeworkTracker";
import { GradeBadge } from "@/components/GradeBadge";
import { ArrowLeft, CalendarX, MessageSquare, ChevronDown, ChevronRight, Table as TableIcon, LayoutGrid } from "lucide-react";
import { PdfReportButton } from "@/components/PdfReportButton";

// === Card view block (existing) ===
const CycleBlock = ({ label, cycle, computedGrade, hwGrade, testIdPrefix }) => (
    <div className="border border-slate-200 rounded-xl p-4 bg-bone/40">
        <div className="flex items-center justify-between mb-3">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Cikli</div>
                <div className="font-heading font-bold text-slate-800">{label}</div>
            </div>
            <GradeBadge grade={computedGrade} size="sm" testId={`${testIdPrefix}-grade`} />
        </div>
        <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Detyrat (10)</div>
            <div className="text-[11px] font-bold text-oxford" data-testid={`${testIdPrefix}-hw-points`}>{hwGrade.toFixed(2)} / 5</div>
        </div>
        <HomeworkTracker statuses={cycle.homework} testIdPrefix={testIdPrefix} />
        <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="flex items-center justify-between gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Testi</span>
                <span className="text-sm font-bold text-slate-800">{cycle.test_score == null ? "—" : `${cycle.test_score}/5`}</span>
            </div>
            <div className="flex items-center justify-between gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Projekti</span>
                <span className="text-sm font-bold text-slate-800">{cycle.project_score == null ? "—" : `${cycle.project_score}/5`}</span>
            </div>
            <div className="flex items-center justify-between gap-1.5" title="Opsionale">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Goje</span>
                <span className="text-sm font-bold text-slate-800">{cycle.oral_score == null ? "—" : `${cycle.oral_score}/5`}</span>
            </div>
        </div>
    </div>
);

const SubjectCard = ({ record, semester }) => {
    const sem = record.semesters[String(semester)];
    const computed = record.computed[String(semester)];
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`subject-card-${record.subject_id}-sem${semester}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {record.subject_type === "professional" ? "Lëndë Profesionale" : "Lëndë e Përgjithshme"}
                    </div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mt-1 truncate">{record.subject_name}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {record.absences_hours > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta text-[11px] font-bold border border-terracotta/30">
                                <CalendarX className="w-3 h-3" /> {record.absences_hours} orë
                            </span>
                        )}
                        {record.comments_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-seablue/10 text-seablue text-[11px] font-bold border border-seablue/30">
                                <MessageSquare className="w-3 h-3" /> {record.comments_count}
                            </span>
                        )}
                    </div>
                </div>
                <div className="text-center shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Përfund.</div>
                    <GradeBadge grade={computed.final} size="lg" testId={`subject-final-${record.subject_id}-sem${semester}`} />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CycleBlock label="Nota 1" cycle={sem["1"]} computedGrade={computed.grade1} hwGrade={computed.hw1} testIdPrefix={`hw-${record.subject_id}-sem${semester}-c1`} />
                <CycleBlock label="Nota 2" cycle={sem["2"]} computedGrade={computed.grade2} hwGrade={computed.hw2} testIdPrefix={`hw-${record.subject_id}-sem${semester}-c2`} />
            </div>
        </div>
    );
};

// === Table view (compact, expandable) ===
const SubjectTableRow = ({ record, semester }) => {
    const [open, setOpen] = useState(false);
    const sem = record.semesters[String(semester)];
    const computed = record.computed[String(semester)];
    return (
        <>
            <tr className="border-b border-slate-200 hover:bg-bone/30 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`subject-row-${record.subject_id}-sem${semester}`}>
                <td className="px-3 py-3">
                    {open ? <ChevronDown className="w-4 h-4 text-oxford" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </td>
                <td className="px-3 py-3">
                    <div className="font-bold text-slate-900 text-sm">{record.subject_name}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{record.subject_type === "professional" ? "Profesionale" : "Përgjithshme"}</div>
                </td>
                <td className="px-2 py-3 text-center"><GradeBadge grade={computed.grade1} size="sm" /></td>
                <td className="px-2 py-3 text-center"><GradeBadge grade={computed.grade2} size="sm" /></td>
                <td className="px-2 py-3 text-center"><GradeBadge grade={computed.final} size="sm" testId={`subject-final-${record.subject_id}-sem${semester}`} /></td>
                <td className="px-2 py-3 text-center">
                    <div className="inline-flex items-center gap-1">
                        {record.absences_hours > 0 && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-terracotta/10 text-terracotta text-[10px] font-bold"><CalendarX className="w-2.5 h-2.5" />{record.absences_hours}h</span>}
                        {record.comments_count > 0 && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-seablue/10 text-seablue text-[10px] font-bold"><MessageSquare className="w-2.5 h-2.5" />{record.comments_count}</span>}
                    </div>
                </td>
            </tr>
            {open && (
                <tr className="bg-bone/20 border-b border-slate-200" data-testid={`subject-row-${record.subject_id}-sem${semester}-expanded`}>
                    <td></td>
                    <td colSpan={5} className="px-3 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {["1", "2"].map((c) => (
                                <div key={c} className="bg-white border border-slate-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Nota {c}</div>
                                        <div className="text-[11px] font-bold text-oxford">{computed[`hw${c}`].toFixed(2)} / 5</div>
                                    </div>
                                    <HomeworkTracker statuses={sem[c].homework} testIdPrefix={`tbl-${record.subject_id}-s${semester}-c${c}`} />
                                    <div className="mt-2 grid grid-cols-3 gap-1 text-xs">
                                        <span><span className="text-slate-500">Testi:</span> <span className="font-bold">{sem[c].test_score ?? "—"}/5</span></span>
                                        <span><span className="text-slate-500">Projekti:</span> <span className="font-bold">{sem[c].project_score ?? "—"}/5</span></span>
                                        <span><span className="text-slate-500">Goje:</span> <span className="font-bold">{sem[c].oral_score ?? "—"}/5</span></span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const SubjectTable = ({ records, semester }) => (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`subject-table-sem${semester}`}>
        <table className="w-full">
            <thead className="bg-bone/60 text-[10px] uppercase tracking-widest text-slate-600 font-bold">
                <tr>
                    <th className="w-8"></th>
                    <th className="text-left px-3 py-3">Lënda</th>
                    <th className="px-2 py-3">Nota 1</th>
                    <th className="px-2 py-3">Nota 2</th>
                    <th className="px-2 py-3">Përfund.</th>
                    <th className="px-2 py-3">Info</th>
                </tr>
            </thead>
            <tbody>
                {records.map((r) => <SubjectTableRow key={r.id} record={r} semester={semester} />)}
            </tbody>
        </table>
    </div>
);

export default function StudentSubjects() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tableView, setTableView] = useState(true); // default to compact table

    useEffect(() => {
        setLoading(true);
        api.get(`/students/${id}/records`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [id]);

    const overallSem = useMemo(() => {
        if (!data) return { 1: null, 2: null };
        const calc = (sem) => {
            const finals = data.records.map((r) => r.computed[sem].final).filter((v) => v != null);
            if (finals.length === 0) return null;
            return Math.round((finals.reduce((a, b) => a + b, 0) / finals.length) * 10) / 10;
        };
        return { 1: calc("1"), 2: calc("2") };
    }, [data]);

    return (
        <Layout>
            <Link to="/parent" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="back-to-parent">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            {loading ? <div className="text-slate-500" data-testid="student-loading">Duke ngarkuar…</div> : !data ? <div className="text-slate-500">Nuk u gjet.</div> : (
                <>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Notat sipas lëndës</div>
                            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{data.student.name}</h1>
                            <p className="text-slate-600 mt-2 text-sm">Viti {data.student.year} • {data.records.length} lëndë</p>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3 self-start md:self-auto">
                            <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 flex gap-4 sm:gap-6">
                                <div className="text-center">
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sem 1</div>
                                    <GradeBadge grade={overallSem[1]} size="md" testId="overall-sem1" />
                                </div>
                                <div className="text-center">
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sem 2</div>
                                    <GradeBadge grade={overallSem[2]} size="md" testId="overall-sem2" />
                                </div>
                            </div>
                            <PdfReportButton studentId={id} />
                        </div>
                    </div>

                    <div className="mb-4 flex flex-wrap items-center gap-3">
                        <HomeworkLegend />
                    </div>

                    <Tabs defaultValue="1" className="w-full">
                        <div className="flex items-center justify-between gap-3 mb-4">
                            <TabsList className="bg-white border border-slate-200 h-11 p-1">
                                <TabsTrigger value="1" data-testid="semester-1-tab" className="px-4 sm:px-6 data-[state=active]:bg-oxford data-[state=active]:text-white">Semestri 1</TabsTrigger>
                                <TabsTrigger value="2" data-testid="semester-2-tab" className="px-4 sm:px-6 data-[state=active]:bg-oxford data-[state=active]:text-white">Semestri 2</TabsTrigger>
                            </TabsList>
                            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5">
                                <LayoutGrid className={`w-4 h-4 ${tableView ? "text-slate-400" : "text-oxford"}`} />
                                <Switch checked={tableView} onCheckedChange={setTableView} data-testid="view-toggle-switch" />
                                <TableIcon className={`w-4 h-4 ${tableView ? "text-oxford" : "text-slate-400"}`} />
                            </div>
                        </div>
                        {["1", "2"].map((s) => (
                            <TabsContent key={s} value={s} className="mt-2">
                                {tableView ? (
                                    <SubjectTable records={data.records} semester={s} />
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                        {data.records.map((r) => <SubjectCard key={r.id} record={r} semester={s} />)}
                                    </div>
                                )}
                            </TabsContent>
                        ))}
                    </Tabs>
                </>
            )}
        </Layout>
    );
}
